import { Box, Paper, Typography, Chip, Grid } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DevicesIcon from '@mui/icons-material/Devices';
import logo from '../assets/logo.jpeg';

// Hardcoded for now — will be replaced with real DB data
const systems = [
  {
    id: 1,
    name: 'Joint Operations Network',
    cage_code: '1ABC2',
    classification: 'UNCLASSIFIED//FOUO',
    ato_status: 'ACTIVE',
    ato_expires: '2026-09-30',
    assets: 342,
    risk_score: 78,
    open_alerts: 14,
  },
  {
    id: 2,
    name: 'Command & Control System',
    cage_code: '3DEF4',
    classification: 'SECRET',
    ato_status: 'ACTIVE',
    ato_expires: '2027-03-15',
    assets: 128,
    risk_score: 91,
    open_alerts: 3,
  },
  {
    id: 3,
    name: 'Logistics Management Platform',
    cage_code: '5GHI6',
    classification: 'UNCLASSIFIED',
    ato_status: 'PENDING',
    ato_expires: null,
    assets: 87,
    risk_score: 64,
    open_alerts: 27,
  },
];

function RiskBadge({ score }) {
  const color = score >= 85 ? '#4caf50' : score >= 70 ? '#ff9800' : '#f44336';
  const label = score >= 85 ? 'Low Risk' : score >= 70 ? 'Medium Risk' : 'High Risk';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700 }}>{score}</Typography>
      <Chip label={label} size="small" sx={{ backgroundColor: color, color: '#fff', fontWeight: 700 }} />
    </Box>
  );
}

export default function SystemSelect({ onSelect }) {
  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 6 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 1 }}>
        <img src={logo} alt="Eighth Technologies" style={{ width: 48, height: 48, borderRadius: '50%' }} />
        <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700, letterSpacing: 3 }}>ASPYN</Typography>
      </Box>
      <Typography variant="body1" sx={{ color: '#7a8fa6', marginBottom: 6 }}>
        Select a system to view
      </Typography>

      {/* System Cards */}
      <Grid container spacing={3} sx={{ maxWidth: 1100 }}>
        {systems.map((system) => (
          <Grid item xs={12} md={4} key={system.id}>
            <Paper
              onClick={() => onSelect(system)}
              sx={{
                backgroundColor: '#111827',
                border: '1px solid #1e3a5f',
                borderRadius: 2,
                padding: 3,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  border: '1px solid #00bcd4',
                  backgroundColor: '#0d1f33',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {/* System name + CAGE */}
              <Box sx={{ marginBottom: 2 }}>
                <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700 }}>{system.name}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#7a8fa6' }}>CAGE: {system.cage_code}</Typography>
                  <Chip label={system.classification} size="small" sx={{ backgroundColor: '#1e3a5f', color: '#c0cfe0', fontSize: 10 }} />
                </Box>
              </Box>

              {/* Divider */}
              <Box sx={{ height: '1px', backgroundColor: '#1e3a5f', marginBottom: 2 }} />

              {/* Stats */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                {/* ATO Status */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VerifiedIcon sx={{ color: system.ato_status === 'ACTIVE' ? '#4caf50' : '#ff9800', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#7a8fa6' }}>ATO Status</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Chip
                      label={system.ato_status}
                      size="small"
                      sx={{ backgroundColor: system.ato_status === 'ACTIVE' ? '#4caf50' : '#ff9800', color: '#fff', fontWeight: 700 }}
                    />
                    {system.ato_expires && (
                      <Typography variant="caption" sx={{ color: '#7a8fa6', display: 'block' }}>Expires {system.ato_expires}</Typography>
                    )}
                  </Box>
                </Box>

                {/* Risk Score */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SecurityIcon sx={{ color: '#00bcd4', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#7a8fa6' }}>Risk Score</Typography>
                  </Box>
                  <RiskBadge score={system.risk_score} />
                </Box>

                {/* Assets */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DevicesIcon sx={{ color: '#00bcd4', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#7a8fa6' }}>Assets</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 700 }}>{system.assets}</Typography>
                </Box>

                {/* Open Alerts */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon sx={{ color: system.open_alerts > 10 ? '#f44336' : '#ff9800', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#7a8fa6' }}>Open Alerts</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: system.open_alerts > 10 ? '#f44336' : '#ff9800', fontWeight: 700 }}>{system.open_alerts}</Typography>
                </Box>

              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

    </Box>
  );
}
